import { normalizeCostSettings } from 'utils/form';
import { request } from 'utils/api';
import { EvseController } from 'components/modals/EditEvseController/types';
import { useSanitizeCostSettingsFeatures } from 'components/tariffs/features';
import { useState } from 'react';
import { EditEvseControllerEvseId } from '../types';

export default function useSaveEvseController(
  original: EvseController,
  evseController: Partial<EvseController>,
  currency: string,
  onError?: (error) => void,
  close?: () => void,
  onClose?: () => void,
  evseIdAction?: EditEvseControllerEvseId
) {
  const [loading, setLoading] = useState(false);
  const sanitizeCostSettingsFeatures = useSanitizeCostSettingsFeatures();

  const generateNewEvseId = async () => {
    const data = await request({
      method: 'POST',
      path: `/1/evse-controllers/assign-new-evseid`,
      body: {
        evseControllerId: evseController.id,
      },
    });

    return {
      evseId: data.evseId,
      connectors: data.connectors,
      evseIdMode: data.evseIdMode,
    };
  };

  const setCustomStationId = async (stationId: string) => {
    const data = await request({
      method: 'POST',
      path: `/1/evse-controllers/set-custom-evse-id`,
      body: {
        evseControllerId: evseController.id,
        customEvseId: stationId,
      },
    });

    return {
      evseId: data.evseId,
      connectors: data.connectors,
      evseIdMode: data.evseIdMode,
    };
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      if (evseIdAction) {
        switch (evseIdAction?.action) {
          case 'enterStationIdFromSticker':
            evseController.evseId = evseIdAction.stationId;
            break;
          case 'generateNewStationId': {
            let { evseId, connectors, evseIdMode } = await generateNewEvseId();
            evseController.evseId = evseId;
            evseController.evseIdMode = evseIdMode;
            evseController.connectors = connectors;
            break;
          }
          case 'setCustomStationId': {
            if (!evseController.locationId) {
              throw new Error(
                'Location must be set in order to generate a Custom Station ID!'
              );
            }

            let { evseId, connectors, evseIdMode } = await setCustomStationId(
              evseIdAction.stationId as string
            );
            evseController.evseId = evseId;
            evseController.evseIdMode = evseIdMode;
            evseController.connectors = connectors;
            break;
          }
        }
      }

      const costSettings = normalizeCostSettings(evseController.costSettings);
      sanitizeCostSettingsFeatures(costSettings, currency);
      const body = {
        ...evseController,
        costSettings,
        pairedChargeCard: undefined,
        evseOperationalStatusId: undefined,
      };
      const isUpdate = !!original.id;
      const method = isUpdate ? 'PATCH' : 'POST';
      const path = isUpdate
        ? `/1/evse-controllers/${original.id}`
        : '/1/evse-controllers';

      await request({ method, path, body });
      close?.();
      onClose?.();
    } catch (error) {
      onError?.(error);
      setLoading(false);
    }
  };

  return {
    handleSubmit,
    loading,
  };
}
